/**
 * One socket for the application, and the hook screens use to read a topic. Task 33.
 *
 * `socket.ts` owns the protocol; this owns three things it deliberately does not know about.
 *
 * ## 1. The refetch that `API-Spec.md` requires on reconnect
 *
 * > **on reconnect the client refetches state over REST rather than assuming continuity**,
 * > because a gap in a push stream is invisible unless you check.
 *
 * `queryClient.invalidateQueries()` with no filter, which is the blunt reading and the right
 * one. A selective invalidation would be a second list of "which queries are fed by pushes",
 * maintained by hand, and the entry someone forgets is a panel left showing a number from
 * before the gap with nothing on screen to say so. Refetching everything after a reconnect
 * costs a handful of requests on an event that is rare by construction.
 *
 * ## 2. The socket exists only while somebody is signed in
 *
 * The upgrade is authenticated by the session cookie, so a socket opened before sign-in is
 * refused and one left open after sign-out is closed by the server anyway. Starting it on
 * `status === 'in'` means the reconnect loop never runs against a login screen — which it
 * otherwise would, at the backoff cap, for as long as the tab sat there.
 *
 * ## 3. A closed socket is reported, not hidden
 *
 * A chart whose live updates have stopped looks exactly like a market that has stopped moving,
 * and in this product that is the wrong thing to let an operator believe. `useLiveConnection`
 * is what screens read to say so. There is no animation on it: `--dur-staleness` is `0ms` and
 * a trust signal that fades has an interval during which it is ambiguous.
 */

import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAuth } from '../auth/AuthProvider';
import { sessionLost } from '../auth/sessionLost';
import { LiveSocket, type ConnectionState, type PushHandler } from './socket';

type DroppedHandler = (count: number) => void;

interface LiveContextValue {
  readonly state: ConnectionState;
  /** Topics the server accepted and cannot deliver yet. Screens may say so. */
  readonly unavailable: readonly string[];
  readonly subscribe: (topic: string, handler: PushHandler) => () => void;
  /** Listen for the server's `logs.dropped` marker. See `useLogsDropped`. */
  readonly subscribeDropped: (handler: DroppedHandler) => () => void;
}

const LiveContext = createContext<LiveContextValue | null>(null);

export function LiveProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { status } = useAuth();
  const queryClient = useQueryClient();
  const [state, setState] = useState<ConnectionState>('closed');
  const [unavailable, setUnavailable] = useState<readonly string[]>([]);

  // The socket outlives every render and must not be rebuilt by one. A `useState` initialiser
  // would be called twice under `StrictMode` and leave an orphan socket reconnecting forever.
  const ref = useRef<LiveSocket | null>(null);
  /**
   * Bumped whenever the socket is replaced, purely so `subscribe`'s identity changes with it.
   *
   * **Hardening, not a bug fix — no failure was reproduced.** React runs child effects before
   * parent effects, so a screen mounting in the same commit as this provider calls `subscribe`
   * while `ref.current` is still `null`, gets the no-op below, and never retries. Today that is
   * harmless only *incidentally*: `MarketsScreen` derives its topic from an instrument query, so
   * the topic is `null` on the first pass and the effect re-runs once the data lands. Tested by
   * remounting the screen against a warm cache — the subscription survived.
   *
   * Nothing enforces that incidental property. A screen with a topic known at mount, or one more
   * dependency added to the effect below, turns it into "the chart silently never updates" —
   * which is indistinguishable from a quiet market. Making `subscribe` change identity with the
   * socket means consumers re-subscribe for real, whatever the mount order.
   */
  const [generation, setGeneration] = useState(0);

  /**
   * Listeners for `logs.dropped`, held in a ref rather than in state.
   *
   * Not a `useState` list: the marker arrives *during* a burst the terminal is already
   * rendering, and putting the listener set in state would re-render every consumer of this
   * context each time the terminal mounts. The set is a side channel, not part of the value.
   */
  const droppedListeners = useRef(new Set<DroppedHandler>());

  useEffect(() => {
    if (status !== 'in') return;

    const socket = new LiveSocket(undefined, {
      onState: setState,
      onLogsDropped: (count) => {
        for (const listener of [...droppedListeners.current]) listener(count);
      },
      onReconnect: () => {
        // Everything, unfiltered. See the module docstring.
        void queryClient.invalidateQueries();
      },
      onUnavailable: (topics) =>
        setUnavailable((current) => [...new Set([...current, ...topics])]),
      // Routed through the app's single 401 handler rather than handled here. `AuthProvider`
      // already owns "the session ended" for every other transport, and a second implementation
      // beside it is two answers to one question.
      onSessionEnded: sessionLost,
    });
    ref.current = socket;
    socket.start();
    // After the ref, so a consumer re-running on this change finds a socket to talk to.
    setGeneration((n) => n + 1);

    return () => {
      socket.stop();
      ref.current = null;
      setState('closed');
    };
  }, [status, queryClient]);

  const subscribe = useCallback(
    (topic: string, handler: PushHandler) => {
      const socket = ref.current;
      if (!socket) return () => {};
      return socket.on(topic, handler);
    },
    // `generation` is not read in the body and is the entire point: it changes this function's
    // identity when the socket is replaced, which re-runs every `useLiveTopic` effect. Without
    // it a consumer that subscribed before the socket existed stays silently unsubscribed.
    [generation],
  );

  const subscribeDropped = useCallback((handler: DroppedHandler) => {
    droppedListeners.current.add(handler);
    return () => {
      droppedListeners.current.delete(handler);
    };
  }, []);

  const value = useMemo(
    () => ({ state, unavailable, subscribe, subscribeDropped }),
    [state, unavailable, subscribe, subscribeDropped],
  );

  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

function useLive(): LiveContextValue {
  const value = useContext(LiveContext);
  if (!value) throw new Error('useLive must be used inside <LiveProvider>');
  return value;
}

/** The connection, for a screen that needs to say whether its numbers are still arriving. */
export function useLiveConnection(): { state: ConnectionState; live: boolean } {
  const { state } = useLive();
  return { state, live: state === 'open' };
}

/**
 * Subscribe to one topic for as long as the component is mounted.
 *
 * `handler` is held in a ref rather than being a dependency of the effect. A screen's handler
 * closes over its own state and is therefore a new function on every render; making it a
 * dependency would unsubscribe and resubscribe on each one, which on a topic the server is
 * pushing to means a subscribe/unsubscribe pair per frame received.
 *
 * `topic` may be `null` — a screen with nothing selected yet has nothing to subscribe to, and
 * `null` is cheaper to express here than a conditional hook, which is not allowed anyway.
 */
export function useLiveTopic(topic: string | null, handler: PushHandler): void {
  const { subscribe } = useLive();
  const latest = useRef(handler);
  latest.current = handler;

  useEffect(() => {
    if (!topic) return;
    return subscribe(topic, (data) => latest.current(data));
  }, [topic, subscribe]);
}

/**
 * Listen for the server's `logs.dropped` marker.
 *
 * Separate from `useLiveTopic` because the marker is not a push and is not on a topic — it is
 * the hub reporting its own outbox overflowed. The terminal renders it inline as a gap, which
 * is the only place in the UI where "we lost some" is a thing worth drawing.
 */
export function useLogsDropped(handler: (count: number) => void): void {
  const { subscribeDropped } = useLive();
  const latest = useRef(handler);
  latest.current = handler;

  useEffect(() => subscribeDropped((count) => latest.current(count)), [subscribeDropped]);
}
